import React, { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react'
import { AppstoreFilled, EditOutlined, BarChartOutlined } from '@ant-design/icons'
import { useAppState } from '../hooks/use-mobile-service'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import './bottom-tab-bar.scss'

type TabId = 'home' | 'compose' | 'insights'

interface TabDefinition {
    id: TabId
    label: string
    icon: React.ReactNode
    getRoute: (domainId: number) => string
    matchPattern: RegExp
    /** If provided, tab is only shown when the user has this ability */
    requiredAction?: AbilityAction
    requiredSubject?: SubjectEntity
}

const TAB_DEFINITIONS: TabDefinition[] = [
    {
        id: 'home',
        label: 'Dashboard',
        icon: <AppstoreFilled aria-hidden="true" />,
        getRoute: (domainId) => `/domains/${domainId}/dashboard`,
        matchPattern: /\/domains\/\d+\/dashboard/,
    },
    {
        id: 'compose',
        label: 'Compose',
        icon: <EditOutlined aria-hidden="true" />,
        getRoute: (domainId) => `/domains/${domainId}/notifications/new`,
        matchPattern: /\/domains\/\d+\/notifications\/(new|edit)/,
        requiredAction: AbilityAction.CREATE,
        requiredSubject: SubjectEntity.NOTIFICATION,
    },
    {
        id: 'insights',
        label: 'Insights',
        icon: <BarChartOutlined aria-hidden="true" />,
        getRoute: (domainId) => `/domains/${domainId}/insights`,
        matchPattern: /\/domains\/\d+\/insights/,
    },
]

export const BottomTabBar: React.FC = observer(() => {
    const appState = useAppState()
    const history = useHistory()
    const location = useLocation()

    // Filter tabs based on user permissions.
    // Access abilities getter directly so MobX observer tracks the observable reference.
    const _abilities = appState.abilityStore.abilities
    const visibleTabs = TAB_DEFINITIONS.filter((tab) => {
        if (!tab.requiredAction || !tab.requiredSubject) return true
        return (
            _abilities?.can(tab.requiredAction, appState.abilityStore.getDomainOwnedIdentityFor(tab.requiredSubject)) ??
            false
        )
    })

    const activeTab = useMemo((): TabId => {
        const path = location.pathname
        for (const tab of visibleTabs) {
            if (tab.matchPattern.test(path)) {
                return tab.id
            }
        }
        return 'home'
    }, [location.pathname, visibleTabs])

    const handleTabClick = useCallback(
        (tab: TabDefinition) => {
            const domainId = appState.currentDomain?.id
            if (domainId) {
                history.push(tab.getRoute(domainId))
            }
        },
        [appState, history],
    )

    return (
        <nav className="mobile-tab-bar" role="tablist" aria-label="Main navigation">
            {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.id
                return (
                    <button
                        key={tab.id}
                        className={`mobile-tab-bar__tab ${isActive ? 'mobile-tab-bar__tab--active' : ''}`}
                        role="tab"
                        aria-selected={isActive}
                        aria-label={tab.label}
                        onClick={() => handleTabClick(tab)}
                        type="button"
                    >
                        <span className="mobile-tab-bar__tab-icon">{tab.icon}</span>
                        <span className="mobile-tab-bar__tab-label">{tab.label}</span>
                    </button>
                )
            })}
        </nav>
    )
})

BottomTabBar.displayName = 'BottomTabBar'
